import { ActivePromotion, ISkuQuantity, PromotionType } from '../../graphQL/commons'
import {
  ICartProduct,
  IConditionAction,
  Condition,
  IConfig,
  ITransformCartProductsParams,
} from '../../interfaces'
import { getActivePromotionsFields } from '../common/getPromotionFields'
import { promotionsMap } from './getPromotionDetailsByType'
import { transformProductArray } from './setPromotionUiDetailsCart'

/**
 * Prefix for SKUs of free-good products added for the GMV (Total Order) case.
 */
export const PREFIX_GMV_FREE_PRODUCT = 'GMV_FREE_PRODUCT_'

/**
 * Key for products dictionary on parent-child mapping objects if no specific packageName used.
 */
export const PRODUCTS_PROP_NAME = 'products'

/**
 * Layout for mapping parent SKUs/packages to freegood child lists.
 */
type ProductsByPackage = Record<string, ICartProduct[] | undefined>
type ParentChildMap = Record<string, ProductsByPackage | undefined>

/**
 * Determines if the given SKU entry matches a given product (by SKU and packageName).
 */
const isSkuQuantitySameAsProduct = (
  { sku, packageName }: ISkuQuantity,
  { sku: productSku, package: productPackage }: ICartProduct
) =>
  sku === productSku && (productPackage?.name ?? null) === packageName

/**
 * Set of conditions for whether a promotion should generate item extras for UI (freegoods).
 */
const conditionsUsePromotionAsItemExtra: Condition<ActivePromotion>[] = [
  ({ freeGoods }) => Number(freeGoods?.length) > 0,
]

/**
 * Args shape for match-product-to-promotion mapping actions.
 */
interface IMatchProductPromotionArgs {
  activePromotionMatchProductAsFreegood?: boolean
  cartProduct: ICartProduct
  activePromotion: ActivePromotion
  parentChildMapping: ParentChildMap
  config?: IConfig
  cartProducts?: ICartProduct[]
}

/**
 * Defines how to map parents in the cart to their children (freegoods) based on promotion logic.
 * Supports the special GMV/Total Order promo, and regular child assignment by consumed SKU.
 */
const conditionsMapParents: IConditionAction<
  IMatchProductPromotionArgs,
  ParentChildMap
>[] = [
  // GMV/Total Order Promotion: Free items added at root under special prefix, readOnly for UI.
  {
    condition: ({
      activePromotion: {
        promotion: { type },
      },
      activePromotionMatchProductAsFreegood,
    }) =>
      !!activePromotionMatchProductAsFreegood && type === PromotionType.TOTAL,
    transformAction: ({
      activePromotion,
      cartProduct: { sku: cartProductSku },
      cartProduct,
      parentChildMapping,
      config,
    }) => {
      const {
        fields: { customerGetsQuantity, type },
        freeGoods,
      } = getActivePromotionsFields(activePromotion)
      const freegoods = freeGoods.filter(({ items }) =>
        items?.some(({ sku: currentSku }) => currentSku === cartProductSku),
      )
      const promotionalPrice = 0
      const promotionalQty = freegoods.reduce<number>(
        (accFreegoods, { items }) => {
          const totalItems =
            items?.reduce<number>(
              (accItems, { sku, quantity }) =>
                sku === cartProductSku ? accItems + (quantity || 0) : accItems,
              accFreegoods,
            ) || 0
          return totalItems
        },
        0,
      )
      const product: ICartProduct = {
        ...cartProduct,
        sku: `${PREFIX_GMV_FREE_PRODUCT}${cartProductSku}`,
        readOnly: true,
        quantity: promotionalQty,
        promotion: {
          value: promotionalPrice,
          ...promotionsMap[type](!!customerGetsQuantity)({
            activePromotion,
            cartProduct: {
              ...cartProduct,
              promotionalQty,
            },
            config,
          }),
        },
      }
      const existingChildren: ICartProduct[] =
        parentChildMapping[PromotionType.TOTAL]?.products || []

      return {
        ...parentChildMapping,
        [PromotionType.TOTAL]: { products: [ ...existingChildren, product ] },
      }
    },
  },
  // Regular freegoods: Add children to correct parent key and propName.
  {
    condition: ({ activePromotionMatchProductAsFreegood }) =>
      !!activePromotionMatchProductAsFreegood,
    transformAction: ({
      activePromotion,
      cartProduct,
      parentChildMapping,
      config,
      cartProducts,
    }) => {
      const {
        fields: { customerGetsQuantity, type },
        freeGoods,
      } = getActivePromotionsFields(activePromotion)
      const freegoods = freeGoods.filter(({ items }) =>
        items?.some((item) => isSkuQuantitySameAsProduct(item, cartProduct)))

      const result: ParentChildMap = freegoods.reduce<ParentChildMap>((acc, freegood) => {
        const freegoodItemQuantity =
          freegood.items?.find(
            (item) => isSkuQuantitySameAsProduct(item, cartProduct),
          )?.quantity || 0

        const promotionalQty = freegoodItemQuantity
        const product: ICartProduct = {
          ...cartProduct,
          readOnly: false,
          quantity: cartProduct.quantity,
          promotion: promotionsMap[type](!!customerGetsQuantity)({
            activePromotion,
            cartProduct: {
              ...cartProduct,
              promotionalQty,
            },
            config,
            cartProducts,
          }),
        }

        const consumedParent = freegood?.consumed?.find(
          ({ sku }) => sku !== undefined,
        )

        if (!!consumedParent?.sku) {
          const { sku, packageName } = consumedParent
          const propForProducts = packageName ?? PRODUCTS_PROP_NAME
          const existingChildren: ICartProduct[] = acc[sku]?.[propForProducts] || []
          const newProductsParent: ParentChildMap = {
            ...acc,
            [sku]: {
              ...acc[sku],
              [propForProducts]: [...existingChildren, product],
            }
          }
          return newProductsParent
        }
        return acc
      }, parentChildMapping)
      return result
    },
  },
]

/**
 * Args for filtering freegood relationships from promotions.
 */
interface IFilterFreegoodsArgs {
  cartProduct: ICartProduct
  activePromotions: ActivePromotion[]
  parentChildMapping: ParentChildMap
  config?: IConfig
  cartProducts?: ICartProduct[]
}

/**
 * Rules for determining which cart products are tied to which freegood (child items) via promotions.
 */
const conditionsFilterFreegoods: IConditionAction<
  IFilterFreegoodsArgs,
  ParentChildMap
>[] = [
  {
    condition: ({ cartProduct: { promotionalQty } }) => promotionalQty > 0,
    transformAction: ({
      cartProduct,
      activePromotions,
      parentChildMapping,
      config,
      cartProducts,
    }) =>
      activePromotions.reduce<ParentChildMap>((acc, activePromotion) => {
        const activePromotionMatchProductAsFreegood =
          !!activePromotion.freeGoods?.some(({ items }) =>
            items?.some(
              (item) => isSkuQuantitySameAsProduct(item, cartProduct),
            )
          )

        const validCondition = conditionsMapParents.find(({ condition }) =>
          condition({
            activePromotionMatchProductAsFreegood,
            cartProduct,
            activePromotion,
            parentChildMapping: acc,
          }),
        )
        return (
          validCondition?.transformAction({
            cartProduct,
            activePromotion,
            parentChildMapping: acc,
            config,
            cartProducts,
          }) || acc
        )
      }, parentChildMapping),
  },
]

/**
 * Builds a mapping of cart product parents to their freegood children according to all eligible promotions.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional platform/config options
 * @returns Parent/Child mapping where parent key is SKU (optionally with package), and children are freegood products
 */
export const mapFreegoodsAndParents = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
): ParentChildMap => {
  const activePromotionsFiltered = activePromotions.filter((activePromotion) =>
    conditionsUsePromotionAsItemExtra.every((condition) =>
      condition(activePromotion),
    ),
  )
  return cartProducts.reduce<ParentChildMap>(
    (acc, cartProduct) =>
      conditionsFilterFreegoods
        .find(({ condition }) =>
          condition({
            cartProduct,
            activePromotions: activePromotionsFiltered,
            parentChildMapping: acc,
          }),
        )
        ?.transformAction({
          cartProduct,
          activePromotions: activePromotionsFiltered,
          parentChildMapping: acc,
          config,
          cartProducts,
        }) || acc,
    {},
  )
}

/**
 * First-level product filter: only products with quantity > 0.
 */
const conditionsProductosFirstLevel: Condition<ICartProduct>[] = [
  ({ quantity }) => quantity > 0,
]

/**
 * Produces the list of cart products, each with possible `itemsExtra` sub-items
 * (the "freegoods" belonging to that cart item), based on current promotions.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional options/config
 * @returns List of cart products, with any attached sub-items for UI
 */
export const getCartProductsWithSubitems = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
): ICartProduct[] => {
  let parentChildMapping = mapFreegoodsAndParents(
    cartProducts,
    activePromotions,
    config,
  )

  // Optionally disables group display of freegoods, if feature flag set in config.options
  if (config?.options?.freegoodAsCartGroup) {
    parentChildMapping = {}
  }
  const freegoodsGMV: ICartProduct[] =
    parentChildMapping[PromotionType.TOTAL]?.[PRODUCTS_PROP_NAME] || []
  const filteredProducts = cartProducts
    .filter((cartProduct) =>
      conditionsProductosFirstLevel.every((condition) =>
        condition(cartProduct),
      ),
    )
    .map<ICartProduct>((cartProduct) => {
      const { sku: cartProductSku, package: cartProductPackage } = cartProduct
      const itemsExtraResultFreeGood = parentChildMapping[cartProductSku]?.[cartProductPackage?.name ?? PRODUCTS_PROP_NAME]
      // Remove child assignment after retrieving for one pass
      delete parentChildMapping[cartProductSku]?.[cartProductPackage?.name ?? PRODUCTS_PROP_NAME]
      const result: ICartProduct = {
        ...cartProduct,
        itemsExtra: itemsExtraResultFreeGood,
      }

      return result
    })
  return [...filteredProducts, ...freegoodsGMV]
}

/**
 * Generates a flattened, deduplicated list of all "freegood" products given by promotions.
 * Each freegood item in the output will have an empty description.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional config
 * @returns Flat array of freegood products, deduplicated by SKU & promotion
 */
export const getFreeGoodsList = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
) => {
  const parentChildMapping = mapFreegoodsAndParents(
    cartProducts,
    activePromotions,
    config,
  )

  return Object.keys(parentChildMapping)
    .flatMap((skuKey) => Object.keys(parentChildMapping[skuKey] || {}).flatMap((packageKey) => parentChildMapping[skuKey]?.[packageKey] || []))
    .filter((item): item is ICartProduct => item !== undefined)
    .map((item) => ({ ...item, description: '' }))
    .reduce<ICartProduct[]>((acc, item) => {
      if (acc.find((i) => i.sku === item.sku && item.promotion)) {
        // If there's a duplicate freegood for this SKU, merge promo origins.
        return acc.map((currItem) => {
          if (currItem.sku === item.sku) {
            const promotion = item.promotion
            const promoOrigin = promotion?.promoOrigin ?? []
            const currentPromoOrigin = currItem.promotion?.promoOrigin ?? []
            const newItem = {
              ...item,
              promotion: {
                ...promotion,
                promoOrigin: [...currentPromoOrigin, ...promoOrigin],
              },
            }
            return newItem
          }
          return currItem
        })
      }
      return [...acc, item]
    }, [])
}

/**
 * Filters out all products from the cart that are "freegoods" (awarded via promotion),
 * except for items with promotionalQty > 1 (for some edge-cases).
 *
 * @param params.cartProducts - The cart product array
 * @param params.activePromotions - Promotions active now
 * @returns Array of non-freegood items
 */
export const getCartWithoutFreegoods = ({
  cartProducts,
  activePromotions,
}: ITransformCartProductsParams): ICartProduct[] => {
  return cartProducts.filter((cartProduct) => {
    const activePromotionMatchProductAsFreegood = activePromotions.some(
      ({ freeGoods }) =>
        !!freeGoods?.some(({ items }) =>
          items?.some(
            (item) => isSkuQuantitySameAsProduct(item, cartProduct),
          )
        ),
    )
    return (
      !activePromotionMatchProductAsFreegood ||
      (activePromotionMatchProductAsFreegood && cartProduct.promotionalQty > 1)
    )
  })
}

/**
 * Main function for UI:
 * Transforms the input cart product list to include all needed promotion information, including
 * freegood/product subitems (`itemsExtra`) attached to cart products, and mapping all
 * relevant promotions to the right products for subitem UIs.
 *
 * 1. Filters out promotions that are only "subitem" (freegood) type,
 *    to focus on top-level promotions only.
 * 2. Attaches subitems to cart products (each with relevant promotion object).
 * 3. Reduces through top-level promotions, using a matching transformer for each promo type,
 *    to finalize UI-useful product structure.
 *
 * @param params.cartProducts - The cart product array
 * @param params.activePromotions - All currently active promotions
 * @param params.config - Optional config
 * @returns The transformed array of products, shaped for UI display with subitems and promotion info.
 */
export const setPromotionUiDetailsCartSubitems = ({
  cartProducts,
  activePromotions,
  config,
}: ITransformCartProductsParams): ICartProduct[] => {
  // Only keep top-level promotions (not freegood-only)
  const activePromotionsFiltered = activePromotions.filter(
    (activePromotion) =>
      !conditionsUsePromotionAsItemExtra.every((condition) =>
        condition(activePromotion),
      ),
  )

  const cartProductsWithSubitems = getCartProductsWithSubitems(
    cartProducts,
    activePromotions,
    config,
  )
  const result = activePromotionsFiltered?.reduce<ICartProduct[]>(
    (currentItems, activePromotion) => {
      const activePromotionFields = getActivePromotionsFields(activePromotion)
      return currentItems.map(
        (cartProduct) =>
          transformProductArray
            .find(({ condition }) =>
              condition({
                activePromotionFields,
                cartProduct,
                activePromotion,
                config,
                activePromotions: activePromotionsFiltered,
              }),
            )
            ?.transformAction({
              activePromotionFields,
              cartProduct,
              activePromotion,
              config,
              activePromotions: activePromotionsFiltered,
            }) || cartProduct,
      )
    },
    cartProductsWithSubitems,
  )
  return result
}
